import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getRetainerTranslations } from '@common/i18n/retainer';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { buildUnsignedRetainerPdfFileName } from '@common/utils/retainerDocumentNaming';
import { LEGAL_DOC_DATE_FORMAT } from '@common/utils/retainerPreviewQuery';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import FullScreenLoader from '@/components/FullScreenLoader';
import PDFViewer from '@/components/PdfViewer/PdfViewer';
import { createRetainerAgreementFillUp } from '@/services/ClientSideTemplateFormService';
import { fetchRetainerPreviewPdf } from '@/services/RetainerPreviewService';
import { theme } from '@/theme';

interface Props {
    /** The file ID of the legal document template */
    fileId: string;

    /** The contact ID of the person signing */
    contactId: string;
    /** The email of the person signing */
    contactEmail: string;
    /** The prospect ID associated with this signing */
    prospectId: string;

    /** Optional pre-populated client name */
    initialClientName?: string;
    /** Optional custom prefix for PDF download filenames */
    documentDisplayName?: string | null;

    /** Called when the document has been successfully signed and user clicks Next */
    onComplete(): void;
    /** Called when the modal is closed without completing */
    onClose(): void;
    language?: TemplateFormLanguage;
}

export default function LegalDocumentModal({
    fileId,
    contactId,
    contactEmail,
    prospectId,
    initialClientName = '',
    documentDisplayName = null,
    onComplete,
    onClose,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getRetainerTranslations(language).legalDocument;

    const [clientName, setClientName] = useState<string>(initialClientName);
    const [isCheckedRetainer, setIsCheckedRetainer] = useState(false);
    const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);
    const [signedDownloadFileName, setSignedDownloadFileName] = useState<string | null>(null);
    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(true);
    const [isPreviewReady, setIsPreviewReady] = useState(false);
    const [previewAttempt, setPreviewAttempt] = useState(0);

    const unsignedDownloadFileName = useMemo(
        () => buildUnsignedRetainerPdfFileName(documentDisplayName),
        [documentDisplayName],
    );
    const downloadFileName = signedDownloadFileName ?? unsignedDownloadFileName;

    useEffect(() => {
        if (signedPdfUrl) return;

        if (!prospectId || !contactId) {
            setIsPreviewLoading(false);
            setIsPreviewReady(false);
            setPreviewBlobUrl(null);
            return;
        }

        let cancelled = false;
        setIsPreviewLoading(true);
        setIsPreviewReady(false);
        setPreviewBlobUrl((current) => {
            if (current) {
                URL.revokeObjectURL(current);
            }
            return null;
        });

        fetchRetainerPreviewPdf(fileId, { prospectId, contactId })
            .then((blob) => {
                if (cancelled) {
                    return;
                }
                setPreviewBlobUrl(URL.createObjectURL(blob));
                setIsPreviewReady(true);
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setIsPreviewReady(false);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsPreviewLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [fileId, prospectId, contactId, previewAttempt, signedPdfUrl]); // oxlint-disable-line react-hooks/exhaustive-deps -- This re-runs when retry is clicked

    useEffect(() => {
        return () => {
            if (previewBlobUrl) {
                URL.revokeObjectURL(previewBlobUrl);
            }
        };
    }, [previewBlobUrl]);

    const pdfUrl = signedPdfUrl ?? previewBlobUrl ?? undefined;

    const signMutation = useMutation({
        mutationFn: async (name: string) => {
            const response = await createRetainerAgreementFillUp({
                fileId,
                contactId,
                date: DateTime.now().toFormat(LEGAL_DOC_DATE_FORMAT),
                clientName: name,
                clientEmail: contactEmail,
                prospectId,
            });
            return response.data;
        },
        onSuccess: async (data) => {
            if (data?.fileId) {
                setSignedPdfUrl(`${import.meta.env.VITE_APP_API_URL}/api/v1/files/${data.fileId}`);
            }
            if (data?.fileName) {
                setSignedDownloadFileName(data.fileName);
            }
        },
    });

    const isSigned = signMutation.isSuccess;
    const isSubmitting = signMutation.isPending;
    const canSign = isPreviewReady && Boolean(clientName) && !isSigned;

    const handleAgreeClick = async () => {
        if (!canSign || isSubmitting) return;
        setIsCheckedRetainer(true);
        signMutation.mutate(clientName, {
            onError: () => {
                setIsCheckedRetainer(false);
            },
        });
    };

    const handleNextClick = () => {
        if (!isSigned) return;
        onComplete();
    };

    const agreeDisabledReason = !clientName
        ? t.disabledReasons.enterName
        : isPreviewLoading
          ? t.disabledReasons.documentLoading
          : !isPreviewReady
            ? t.disabledReasons.documentNotReady
            : '';

    return (
        <Dialog
            open
            disableEscapeKeyDown
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            PaperProps={{
                sx: { maxWidth: '90vw', width: '100%' },
            }}
            fullWidth
            scroll="paper"
        >
            <DialogTitle>{t.title}</DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: isMobile ? '90vh' : '80vh',
                    overflow: 'auto',
                }}
            >
                <Box
                    sx={{
                        flex: '1 1 auto',
                        minHeight: '50%',
                        overflow: 'auto',
                    }}
                >
                    {isSubmitting && <FullScreenLoader />}
                    {isPreviewLoading && !signedPdfUrl ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                            <CircularProgress />
                        </Stack>
                    ) : !isPreviewReady && !signedPdfUrl ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            sx={{ height: '100%' }}
                        >
                            <Button
                                variant="outlined"
                                onClick={() => setPreviewAttempt((n) => n + 1)}
                            >
                                {t.retry}
                            </Button>
                        </Stack>
                    ) : (
                        pdfUrl && <PDFViewer pdfUrl={pdfUrl} downloadFileName={downloadFileName} />
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 1,
                        mb: 1,
                        p: 2,
                        border: '1px solid #90caf9',
                        borderRadius: 2,
                        backgroundColor: '#E3F2FD',
                        color: '#0D47A1',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 0.5,
                        }}
                    >
                        <strong>{t.step1Label}</strong> {t.step1Instruction}
                    </Typography>
                    <Typography variant="body2">
                        <strong>{t.step2Label}</strong> {t.step2Instruction}
                    </Typography>
                </Box>

                {signMutation.isError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {t.signError}
                    </Alert>
                )}

                <Box component="form" sx={{ flex: '0 0 auto', mt: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: isMobile ? 1 : 2,
                            mb: 1,
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                label={t.clientFullName}
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                disabled={isSigned}
                                fullWidth
                            />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Tooltip
                                title={agreeDisabledReason}
                                disableHoverListener={!agreeDisabledReason}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isCheckedRetainer}
                                            onChange={handleAgreeClick}
                                        />
                                    }
                                    disabled={!canSign}
                                    label={t.agreementCheckbox}
                                    sx={{
                                        alignItems: 'center',
                                        width: '100%',
                                        m: 0,
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between',
                            gap: isMobile ? 1 : 2,
                            width: '100%',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                label={t.signature}
                                value={clientName}
                                disabled
                                InputProps={{
                                    style: {
                                        fontFamily: '"Dancing Script", cursive',
                                        fontSize: '1rem',
                                        fontWeight: '700',
                                    },
                                }}
                                fullWidth
                            />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <LocalizationProvider dateAdapter={AdapterLuxon}>
                                <DateTimePicker
                                    label={t.date}
                                    value={DateTime.now()}
                                    format={LEGAL_DOC_DATE_FORMAT}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    disabled
                                    readOnly
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 1,
                }}
            >
                <span style={{ width: isMobile ? '100%' : 'auto' }}>
                    <LoadingButton
                        variant="contained"
                        loading={false}
                        onClick={handleNextClick}
                        disabled={!isSigned}
                        fullWidth={isMobile}
                    >
                        {t.next}
                    </LoadingButton>
                </span>
            </DialogActions>
        </Dialog>
    );
}
